﻿import React, { useState, ChangeEvent } from 'react';
import cuid from 'cuid';
import Skeleton from '@mui/material/Skeleton';
import { Button, ButtonGroup, Panel } from 'react-bootstrap';
import AutoResizeTextArea from '../../imageprocessing/AutoResizeTextArea';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import PlanOrderApi from '../PlanOrderApi';
import SuccessSnackbar from '../../../../../shared/feedback/SuccessSnackbar';
import FailureSnackbar from '../../../../../shared/feedback/FailureSnackbar';
import { useTelemetryContext } from "../../../../../telemetry/telemetryContext";
import { getUserInfo } from "../../../../../telemetry/utils";
import { AuthenticatedUser } from "../../../../../shared/types";

export type InternalNotesLabels = {
    cancelButton: string;
    editButton: string;
    saveButton: string;
    saveFailure: string;
    saveSuccess: string;
    loadNotesFailure: string;
    title: string;
};

type InternalNotesPropsExternal = {
    labels: InternalNotesLabels;
    planOrderId: number;
    user: AuthenticatedUser;
};

type InternalNotesProps = {
    onSave: (notes: string) => void;
    labels: InternalNotesLabels;
    notes: string;
};

export function InternalNotes({ labels, notes, onSave }: InternalNotesProps) {
    let [isEditMode, setIsEditMode] = useState<boolean>(false);
    let [internalNotes, setInternalNotes] = useState<string>(notes ? notes : '');

    return (
        <Panel
            className="admin-planorder-internal-notes-panel"
            collapsible="false"
            defaultExpanded={true}
        >
            <Panel.Heading className="admin-planorder-internal-notes-heading-container">
                <h4 className="admin-planorder-internal-notes-title">{labels.title}</h4>
                <Button
                    bsSize="small"
                    bsStyle="info"
                    onClick={() => {
                        setIsEditMode((state) => !state);
                    }}
                >
                    <i className="material-icons mode">edit</i> {labels.editButton}
                </Button>
            </Panel.Heading>
            <Panel.Body>
                {isEditMode ? (
                    <>
                        <AutoResizeTextArea
                            name="admin-plan-order-internal-notes"
                            value={internalNotes}
                            maxHeightPx={300}
                            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                                setInternalNotes(event.target.value);
                            }}
                        />
                        <ButtonGroup className="admin-plan-order-internal-notes-buttongroup pull-right">
                            <Button
                                onClick={() => {
                                    setInternalNotes(notes);
                                    setIsEditMode(false);
                                }}
                            >
                                {labels.cancelButton}
                            </Button>
                            <Button
                                bsStyle="primary"
                                onClick={() => {
                                    onSave(internalNotes);
                                    setIsEditMode(false);
                                }}
                            >
                                {labels.saveButton}
                            </Button>
                        </ButtonGroup>
                    </>
                ) : (
                    <p>{internalNotes}</p>
                )}
            </Panel.Body>
        </Panel>
    );
}

function LoadingSkeleton({ labels }) {
    return (
        <Panel
            className="admin-planorder-internal-notes-panel"
            collapsible="false"
            defaultExpanded={true}
        >
            <Panel.Heading className="admin-planorder-internal-notes-heading-container">
                <h4 className="admin-planorder-internal-notes-title">{labels.title}</h4>
            </Panel.Heading>
            <Panel.Body>
                <Skeleton variant="rectangular" width={210} height={60} />
            </Panel.Body>
        </Panel>
    );
}

function InternalNotesDataLayer({ labels, user, planOrderId }: InternalNotesPropsExternal) {
    let [openSuccessSnackbar, setOpenSuccessSnackbar] = useState<boolean>(false);
    let [openSaveFailureSnackbar, setOpenSaveFailureSnackbar] = useState<boolean>(false);
    let [openLoadFailureSnackbar, setOpenLoadFailureSnackbar] = useState<boolean>(false);
    let [saveError, setSaveError] = useState<string>('');
    let { logger } = useTelemetryContext();

    let { isError, isLoading, data, error } = useQuery(
        ['planorder.getInternalNotes', planOrderId],
        () => {
            if (!planOrderId) {
                return Promise.resolve({ internalMessage: null });
            }

            return PlanOrderApi.getInternalNotes(planOrderId);
        },
        {
            onError: (error) => {
                let additionalInfo = {
                    origin: 'AdminPlanOrderForm.InternalNotes',
                    userInfo: getUserInfo(user)
                };
                
                logger.logException(error, additionalInfo);
                setOpenLoadFailureSnackbar(true);
            }
        }
    );

    let queryClient = useQueryClient();

    let saveInternalNotesMutation = useMutation(
        ({ internalNotes }: { internalNotes: string }): Promise<void> =>
            PlanOrderApi.saveInternalNotes(planOrderId, internalNotes),
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries(['planorder.getInternalNotes', planOrderId]);
                setOpenSuccessSnackbar(true);
            },
            onError: async (error: unknown) => {
                let additionalInfo = {
                    origin: 'AdminPlanOrderForm.InternalNotes',
                    userInfo: getUserInfo(user)
                };

                logger.logException(error, additionalInfo);
                /**
                 * setSaveError here is generating a new key in order
                 * for the InternalNotes component to reset state
                 * on error, otherwise the local state of the component
                 * will still appear in the text area.
                 */
                setSaveError(cuid());
                setOpenSaveFailureSnackbar(true);
            }
        }
    );

    function handleSave(newNote: string) {
        saveInternalNotesMutation.mutate({ internalNotes: newNote });
    }

    if (isLoading) {
        return <LoadingSkeleton labels={labels} />;
    }

    return (
        <>
            <InternalNotes
                key={saveError}
                labels={labels}
                notes={data ? data.internalMessage : null}
                onSave={handleSave}
            />
            <SuccessSnackbar
                open={openSuccessSnackbar}
                text={labels.saveSuccess}
                onClose={() => setOpenSuccessSnackbar(false)}
            />
            <FailureSnackbar
                open={openSaveFailureSnackbar}
                text={labels.saveFailure}
                onClose={() => setOpenSaveFailureSnackbar(false)}
            />
            <FailureSnackbar
                open={openLoadFailureSnackbar}
                text={labels.loadNotesFailure}
                onClose={() => setOpenLoadFailureSnackbar(false)}
            />
        </>
    );
}

export default InternalNotesDataLayer;
